/**
 * Why a tax-receipt email did not go out (OP-8127). OPDON persists the dotted
 * `TaxReceipt.Email.<Code>` on the receipt (`lastEmailFailureCode`) and returns it as the
 * `errorCode` of a refused resend; both surfaces translate it through the same keys.
 */
const PREFIX = "TaxReceipt.Email.";
const I18N_PREFIX = "finance.donations.taxReceipt.emailFailure.";
/** Member names of OPDON `TaxReceiptEmailFailureCodes`; every one has a Tolgee key under the i18n prefix. */
export const TAX_RECEIPT_EMAIL_FAILURE_CODES = [
    "NoRecipient",
    "TemplateNotConfigured",
    "DonorInformationMissing",
    "DonorLookupUnavailable",
    "TemplateNotFound",
    "TemplateNotPublished",
    "UnknownVariable",
    "ReceiptLinkMissing",
    "SenderNotConfigured",
    "OpinkRejected",
    "OpinkUnavailable",
    "OutcomeUnknown",
    "OpinkNotConfigured",
    "Unexpected",
];
function knownMember(code) {
    const member = code?.startsWith(PREFIX) ? code.slice(PREFIX.length) : undefined;
    return member && TAX_RECEIPT_EMAIL_FAILURE_CODES.includes(member) ? member : undefined;
}
/** i18n key of a failure code; an unknown or missing code falls back to the generic reason. */
export function taxReceiptEmailFailureKey(code) {
    return `${I18N_PREFIX}${knownMember(code) ?? "Unknown"}`;
}
/** OPDON `TaxReceiptEmailMissingInformation` flag names, as its `[Flags]` ToString writes them. */
const MISSING_INFORMATION_KEYS = {
    DonorNature: "finance.donations.taxReceipt.emailFailure.missingInformation.DonorNature",
    Address: "finance.donations.taxReceipt.emailFailure.missingInformation.Address",
};
/** OPINK error codes OPDON records after the status (`"400 Delivery.From.DomainMismatch"`). */
const OPINK_REJECTION_KEYS = {
    "Delivery.From.DomainMismatch": "finance.donations.taxReceipt.emailFailure.opinkRejection.DomainMismatch",
    PROVIDER_NOT_CONFIGURED: "finance.donations.taxReceipt.emailFailure.opinkRejection.ProviderNotConfigured",
    PROVIDER_DISABLED: "finance.donations.taxReceipt.emailFailure.opinkRejection.ProviderDisabled",
    VALIDATIONERROR: "finance.donations.taxReceipt.emailFailure.opinkRejection.ValidationError",
};
/**
 * The translated reason of a failure code. The detail is data, never shown raw (R29), except the
 * UnknownVariable token names the user typed in the template; a detail no map knows is dropped.
 */
export function taxReceiptEmailFailureReason(t, code, detail) {
    const key = taxReceiptEmailFailureKey(code);
    const raw = detail?.trim() ?? "";
    switch (key) {
        case `${I18N_PREFIX}UnknownVariable`:
            return t(key, { detail: raw });
        case `${I18N_PREFIX}DonorInformationMissing`: {
            const fields = raw
                .split(",")
                .map((flag) => MISSING_INFORMATION_KEYS[flag.trim()])
                .filter((fieldKey) => fieldKey !== undefined)
                .map((fieldKey) => t(fieldKey, {}));
            return fields.length > 0
                ? t(key, { detail: fields.join(", ") })
                : t("finance.donations.taxReceipt.emailFailure.DonorInformationMissingUnspecified", {});
        }
        case `${I18N_PREFIX}OpinkRejected`: {
            const rejectionKey = OPINK_REJECTION_KEYS[raw.split(" ").slice(1).join(" ")];
            return rejectionKey
                ? t(key, { detail: t(rejectionKey, {}) })
                : t("finance.donations.taxReceipt.emailFailure.OpinkRejectedUnspecified", {});
        }
        default:
            return t(key, {});
    }
}
export function isTaxReceiptEmailFailureCode(code) {
    return knownMember(code) !== undefined;
}
/**
 * The detail of a refused resend, or an empty string; render it through `taxReceiptEmailFailureReason`.
 * Read from `errorResult.detail` first: outside Development the BFF replaces the ProblemDetails
 * `detail` with its own generic sentence and forwards only `errorCode` and `errorResult`.
 */
export function apiErrorDetail(err) {
    const data = responseData(err);
    if (data === null)
        return "";
    const result = data.errorResult;
    if (result !== null && typeof result === "object" && "detail" in result && typeof result.detail === "string") {
        return result.detail;
    }
    return typeof data.detail === "string" ? data.detail : "";
}
function responseData(err) {
    if (err !== null &&
        typeof err === "object" &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data !== null &&
        typeof err.response.data === "object") {
        return err.response.data;
    }
    return null;
}
